import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/Popover";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ExternalLink, LogOut, Settings } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";

type AccountMenuProps = {
  /** The clickable element that opens the menu (rendered via `asChild`). */
  trigger: ReactNode;
  align?: "start" | "center" | "end";
  side?: "top" | "bottom" | "left" | "right";
};

/**
 * Shared account popover (Settings / Terms / Privacy / Log out). Rendered in the
 * top-right NavBar on rail-less pages and at the bottom of the workspace rail.
 */
export const AccountMenu = ({
  trigger,
  align = "end",
  side = "bottom",
}: AccountMenuProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/login");
  };

  return (
    <Popover modal open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align={align} side={side} sideOffset={8}>
        <div className="flex flex-col">
          <div className="mb-1 border-b border-zinc-100 px-3 py-2">
            {user.full_name && (
              <p className="truncate text-sm font-medium text-zinc-900">
                {user.full_name}
              </p>
            )}
            {user.email && (
              <p className="truncate text-xs text-zinc-500">{user.email}</p>
            )}
          </div>
          <button
            className="flex cursor-pointer items-center gap-3 rounded-[3px] px-3 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-100"
            onClick={() => {
              setOpen(false);
              navigate("/settings");
            }}
          >
            <Settings className="h-4 w-4 text-zinc-400" />
            Settings
          </button>
          <a
            href="https://gxl.ai/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-[3px] px-3 py-2 text-sm text-zinc-700 transition-colors hover:bg-zinc-100"
          >
            <ExternalLink className="h-4 w-4 text-zinc-400" />
            Terms of Service
            <span className="sr-only"> (opens in new tab)</span>
          </a>
          <a
            href="https://gxl.ai/privacy-notice"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-[3px] px-3 py-2 text-sm text-zinc-700 transition-colors hover:bg-zinc-100"
          >
            <ExternalLink className="h-4 w-4 text-zinc-400" />
            Privacy Notice
            <span className="sr-only"> (opens in new tab)</span>
          </a>
          <button
            className="flex cursor-pointer items-center gap-3 rounded-[3px] px-3 py-2 text-left text-sm text-zinc-700 transition-colors hover:bg-zinc-100"
            onClick={handleLogout}
          >
            <LogOut className="h-4 w-4 text-zinc-400" />
            Log out
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
